import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { platformAPI } from "../services/api";

/**
 * Packs: one subscription paying for several shops of the same owner. A shop in a
 * pack has no subscription of its own — its dates follow the shop that pays, where
 * every payment, plan and suspension is made. Here: which pack a shop is in, and
 * putting it in one or taking it out.
 */
export default function PackCard({ shop, onChange }) {
  const [shops, setShops] = useState([]);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    platformAPI.listShops().then(({ data }) => setShops(data.filter((entry) => !entry.deleted_at)));
  }, []);

  const members = shops.filter((entry) => entry.billing_shop_id === shop.id);
  // Shops of the same owner that pay for themselves: the packs this one can join
  const candidates = shops.filter(
    (entry) => entry.id !== shop.id && !entry.billing_shop_id && entry.owner_id && entry.owner_id === shop.owner_id,
  );

  const setPack = async (billingShopId) => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.setPack(shop.id, billingShopId);
      onChange();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow p-5 mb-6 max-w-2xl">
      <h3 className="font-semibold text-gray-800 mb-2">Pack</h3>

      {shop.billing_shop_id ? (
        <div className="space-y-3 text-sm">
          <p>
            Paid for by{" "}
            <Link to={`/shops/${shop.billing_shop_id}`} className="font-semibold text-primary-700 hover:underline">
              {shop.billing_shop_name}
            </Link>
            's pack. Its plan, dates, payments and suspension are managed there and apply to this shop too.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              if (window.confirm("Take this shop out of the pack? It keeps its current dates; give it a plan of its own next.")) setPack(null);
            }}
            className="text-red-600 font-semibold hover:underline disabled:opacity-50"
          >
            Bill it on its own
          </button>
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          {members.length > 0 ? (
            <p>
              Pays for {members.length + 1} shops (plan covers {shop.plan_shop_limit || 1}):{" "}
              {members.map((member, index) => (
                <span key={member.id}>
                  {index > 0 && ", "}
                  <Link to={`/shops/${member.id}`} className="text-primary-700 hover:underline">
                    {member.name}
                  </Link>
                </span>
              ))}
              . Payments here cover them all.
            </p>
          ) : (
            <p className="text-gray-600">This shop pays for itself.</p>
          )}

          {members.length === 0 && candidates.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <select
                className="px-3 py-2 border border-gray-300 rounded-lg"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">Join the pack of…</option>
                {candidates.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name} ({entry.plan_name || "no plan"}, covers {entry.plan_shop_limit || 1})
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={!target || busy}
                onClick={() => setPack(target)}
                className="rounded-lg bg-primary-600 px-3 py-2 font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
              >
                Join
              </button>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
    </div>
  );
}
